import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { fromZonedTime } from "date-fns-tz";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Filters out system-generated notes from appointment notes
 * Keeps user-entered notes while hiding technical system notes like [System Auto-Approved: ...]
 */
export function filterSystemNotes(notes?: string | null): string | null {
  if (!notes) return null;
  
  // Remove [System ...] patterns (auto-approval, etc.)
  const filtered = notes
    .replace(/\[System[^\]]*\]/g, '')
    .trim();
  
  return filtered || null;
}

export function capitalizeDate(dateString: string): string {
  return dateString.charAt(0).toUpperCase() + dateString.slice(1);
}

/**
 * Returns the emoji for a client type tag
 */
export function getClientTypeEmoji(clientType?: string | null): string {
  switch (clientType) {
    case 'new': return '🆕';
    case 'returning': return '🔄';
    case 'loyal': return '⭐';
    case 'flagged': return '⚠️';
    default: return '';
  }
}

export function formatDatetimeLocal(isoString: string): string {
  // Convert UTC ISO string to local datetime-local format
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Inverse de formatDatetimeLocal : valeur d'un champ datetime-local (heure
 * locale) vers un ISO UTC. Rend null quand la valeur ne donne pas une date
 * valide (champ vidé ou incomplet), au lieu de laisser toISOString() lever.
 */
export function datetimeLocalToIso(value: string): string | null {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Converts a Date object to YYYY-MM-DD format in LOCAL timezone
 * This avoids timezone shifts that occur with toISOString().split("T")[0]
 * USE THIS when storing dates to the database
 */
export function formatDateForDB(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parses a YYYY-MM-DD date string as LOCAL midnight (not UTC midnight)
 * This avoids timezone shifts that occur with parseISO()
 * USE THIS when displaying dates from the database
 */
export function parseDateFromDB(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Safely converts a value to a number with fallback
 * Handles null, undefined, NaN, and invalid string conversions
 * Logs a warning in development when conversion fails
 *
 * @param value - The value to convert to a number
 * @param defaultValue - The fallback value if conversion fails
 * @param context - Optional context string for debugging
 * @returns The converted number or the default value
 */
export const safeNumber = (
  value: unknown,
  defaultValue: number,
  context?: string
): number => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  const num = Number(value);
  if (isNaN(num) || !isFinite(num)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[safeNumber] Invalid number conversion${context ? ` in ${context}` : ''}: ${value}`);
    }
    return defaultValue;
  }
  return num;
};

/**
 * Type guard to check if a value is a valid, finite number
 * Use this before operations that require a valid number
 */
export const isValidNumber = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
};

/**
 * Formats a duration in minutes to a human-readable string.
 * Examples: 0 → "0min", 45 → "45min", 60 → "1h00", 90 → "1h30"
 */
export function formatDuration(minutes: number): string {
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${m}min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}

/**
 * Construit l'instant ABSOLU (UTC) d'un rendez-vous à partir de sa date
 * (YYYY-MM-DD) et son heure (HH:MM) interprétées dans le FUSEAU DU SALON.
 * À utiliser pour tout calcul temporel serveur/critique (ex. fenêtre
 * d'annulation/remboursement d'acompte), jamais l'horloge du navigateur.
 */
export function getAppointmentInstantUTC(
  dateStr: string,
  timeStr: string,
  timeZone: string,
): Date {
  const tz = timeZone && timeZone.trim() ? timeZone : "America/Toronto";
  const hhmm = (timeStr && timeStr.trim() ? timeStr : "00:00").slice(0, 5);
  return fromZonedTime(`${dateStr} ${hhmm}:00`, tz);
}
